import "./EquityAccountViewComponent.scss";
import template from "./EquityAccountViewComponent.html";
import {IComponentOptions, IOnDestroy, IOnInit, IScope, ITimeoutService, translate} from "angular";
import {Observable, ReplaySubject, Subject} from "rxjs";
import {StateService} from "@uirouter/core";
import {equityGridOptions} from "./grid/EquityGridOptions";
import {EquityAccountViewStore} from "../store/EquityAccountViewStore";
import {IGridApi, IUiGridConstants} from "ui-grid";
import {EquityAccountViewStoreFactory} from "../store/EquityAccountViewStoreFactory";
import {AccountsService} from "../../services/AccountsService";
import {AccountName, AccountNumber} from "../../types/SimpleAccountTypes";
import {AccountId, AccountRecord} from "../../types/AccountRecord";
import {isNotNull} from "../../../js/util/UtilFunctions";
import {SystemEventService} from "../../../services/system-state/SystemEventService";
import {SystemEvent} from "../../../services/system-state/SystemEvent";
import {PlaceOrderService} from "../../../portfolio/templates/ticket/services/PlaceOrderService";
import {EquityPositionViewModel, EquityPositionViewModels} from "../models/EquityPositionViewModel";
import {CurrentUserService} from "../../../services/user/CurrentUserService";
import {NgAsideService} from "../../../types/NgAside";
import {LocalStorageService} from "../../../types/NgStorage";
import {AccountStore} from "../../store/AccountStore";
import {ArrayUtil} from "../../../js/util/ArrayUtil";
import {EquityAccountRecord} from "../types/EquityAccountRecord";
import {FeatureNames} from "../../../types/UserConfig";
import {PortfolioType} from "../../../portfolio/models/Portfolio";
import {SimulatedAccountService} from "../services/SimulatedAccountService";
import {BrokerKey} from "../../../types/BrokerKey";
import {OrderSide} from "../../../orders/models/OrderRecord";
import {ClientEventService} from "../../../services/client-events/ClientEventService";
import {isPortfolioClientEventFor} from "app/services/client-events/models/ClientEventRecord";
import {PortfolioService} from "../../../portfolio/service/PortfolioService";
import {CmaSummary} from "../../../portfolio/models/CmaSummary";
import {LanguageService} from "../../../translation/service/LanguageService";
import IGridOptions = uiGrid.IGridOptions;
import IUiGridExporterConstants = uiGrid.exporter.IUiGridExporterConstants;

export const EquityAccountView = "equityPortfolioView";

export function equityAccountView(): IComponentOptions {
    return {
        template,
        controllerAs: "ctrl",
        controller: EquityAccountViewComponent
    };
}

interface EquityAccountStateParams {
    readonly portfolioId: AccountId;
}

interface ControllerScope extends IScope {
    gridContextMenu: any;
}

export class EquityAccountViewComponent implements IOnInit, IOnDestroy {

    public readonly equityGridConfig$ = new ReplaySubject<IGridOptions>(1);

    public activeTab = "account-details";

    public readonly accountId$: Observable<number>;
    public readonly accountName$: Observable<AccountName>;
    public readonly accountNumber$: Observable<AccountNumber>;
    public readonly showClientNotes$: Observable<boolean>;
    public readonly equityAccount$: Observable<EquityAccountRecord>;
    public readonly positions$: Observable<EquityPositionViewModels>;
    public readonly showTradeHistory$: Observable<boolean>;
    public readonly isSimulated$: Observable<boolean>;
    public readonly isClient$: boolean;
    public readonly showReset$: Observable<boolean>;
    public readonly cmaSummary$: Observable<CmaSummary | null>;

    private readonly viewStore: EquityAccountViewStore;

    private readonly gridApi$ = new ReplaySubject<IGridApi>(1);
    private readonly account$: Observable<AccountRecord>;
    private readonly resetAccount$ = new Subject<boolean>();

    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private readonly $scope: ControllerScope,
                       private readonly $state: StateService,
                       private readonly $timeout: ITimeoutService,
                       private readonly $aside: NgAsideService,
                       private readonly $localStorage: LocalStorageService,
                       private readonly $translate: translate.ITranslateService,
                       private readonly accountService: AccountsService,
                       private readonly systemEventService: SystemEventService,
                       private readonly placeOrderService: PlaceOrderService,
                       private readonly currentUserService: CurrentUserService,
                       private readonly uiGridExporterConstants: IUiGridExporterConstants,
                       private readonly accountStore: AccountStore,
                       private readonly simulatedAccountService: SimulatedAccountService,
                       private readonly confirmationService: ConfirmationService,
                       private readonly clientEventService: ClientEventService,
                       private readonly portfolioService: PortfolioService,
                       private readonly languageService: LanguageService,
                       equityAccountViewStoreFactory: EquityAccountViewStoreFactory,
                       $stateParams: EquityAccountStateParams,
                       gridUtil: any,
                       uiGridConstants: IUiGridConstants,
                       screenSize: any) {
        this.viewStore = equityAccountViewStoreFactory();
        this.accountId$ = this.buildAccountId();
        this.accountName$ = this.buildAccountName();
        this.accountNumber$ = this.buildAccountNumber();
        this.account$ = this.buildAccountRecord();
        this.accountStore.selectAccount(+$stateParams.portfolioId);
        this.showClientNotes$ = this.buildShowClientNotes();
        this.showTradeHistory$ = this.buildShowTradeHistory();
        this.equityAccount$ = this.viewStore.account;
        this.positions$ = this.viewStore.positions;
        this.isSimulated$ = this.buildIsSimulated();
        this.showReset$ = this.buildShowReset();
        this.isClient$ = this.currentUserService.isClient();

        const currentFeatures$ = this.currentUserService
                                     .current
                                     .distinctUntilChanged((a, b) => ArrayUtil.matches(a.features, b.features));
        const columnSource$ = Observable.combineLatest(currentFeatures$, this.languageService.current().distinctUntilChanged());

        columnSource$.takeUntil(this.destroyed$)
                     .map(([user, _]) => equityGridOptions($translate, user, gridUtil, uiGridConstants, screenSize))
                     .map((options) => this.addRegistrationCallback(options))
                     .subscribe((config) => this.equityGridConfig$.next(config));

        /* UI-Grid requires you to simply mangle the new data onto the config every time it changes */
        Observable.combineLatest(this.viewStore.positions, this.equityGridConfig$)
                  .takeUntil(this.destroyed$)
                  .subscribe(([positions, config]) => {
                      this.$timeout(() => config.data = positions as any);
                  });

        let ordersEnabled = false;

        currentUserService.current
                          .takeUntil(this.destroyed$)
                          .map((user) => user.hasFeature(FeatureNames.FEATURE_ORDER_ENTRY))
                          .subscribe((enabled) => ordersEnabled = enabled);

        this.cmaSummary$ = this.accountId$
                               .distinctUntilChanged()
                               .switchMap((accountId) => this.portfolioService.cmaDetails(accountId))
                               .takeUntil(this.destroyed$);

        $scope.gridContextMenu = [
            {
                html: `<a tabindex="-1" href="#"><i class="icon-circle-cross"></i> Sell</a>`,
                click: ($itemScope: any) => {
                    const clickedRow = $itemScope.row;
                    const position: EquityPositionViewModel = clickedRow.entity;
                    this.openOrderTicket(position);
                },
                enabled: () => ordersEnabled
            }
        ];
    }

    private openOrderTicket(position: EquityPositionViewModel) {
        if (position != null) {
            const code = position.code;
            const side = OrderSide.LONG_SELL;
            const quantity = position.quantity;

            const order = {
                code,
                side,
                quantity
            };
            this.placeOrderService.openEquityOrderWindow(order);
        }
    }

    private buildAccountId(): Observable<number> {
        return this.viewStore.account.map((a) => a.id);
    }

    private buildAccountName(): Observable<AccountName> {
        return this.viewStore.account.map((a) => a.accountName);
    }

    private buildAccountNumber(): Observable<AccountNumber> {
        return this.viewStore.account.map((a) => a.accountNumber);
    }

    private buildAccountRecord(): Observable<AccountRecord> {
        const updateSource$ = Observable.combineLatest(this.accountStore.current, this.clientEventService.events());
        const updated$ = updateSource$.filter(([account, evt]) => (account != null) && isPortfolioClientEventFor(evt,
                                                                                                                 account.id))
                                      .map(() => true)
                                      .startWith(true);
        const source = Observable.combineLatest(this.accountStore.current, updated$);
        return source.takeUntil(this.destroyed$)
                     .map(([account]) => account)
                     .filter(isNotNull)
                     .map((acc) => acc.id as AccountId)
                     .switchMap((id) => this.retrieveAccount(id))
                     .publishReplay(1)
                     .refCount();
    }

    private retrieveAccount(id: AccountId): Observable<AccountRecord> {
        return Observable.of(id)
                         .flatMap((i) => this.accountService.get(i))
                         .catch(() => Observable.empty());
    }

    private buildShowClientNotes(): Observable<boolean> {
        return this.currentUserService
                   .current
                   .map((u) => u.isAdviser())
                   .publishReplay(1)
                   .refCount();
    }

    private buildShowTradeHistory(): Observable<boolean> {
        return this.currentUserService
                   .current
                   .map((currentUser) => currentUser.hasFeature(FeatureNames.FEATURE_ACCOUNT_PERFORMANCE) || currentUser.isAdmin())
                   .publishReplay(1)
                   .refCount();
    }

    private buildIsSimulated(): Observable<boolean> {
        return this.accountStore
                   .current
                   .filter(isNotNull)
                   .map((a) => a.portfolioType)
                   .map((type) => (type === PortfolioType.SIMULATED) || (type === PortfolioType.SCRATCH))
                   .startWith(false)
                   .publishReplay(1)
                   .refCount();
    }

    private buildShowReset(): Observable<boolean> {
        const isNotGameOrIsAdmin$ = this.currentUserService
                                        .current
                                        .map((u) => (u.broker !== BrokerKey.GAME) || u.isAdmin());
        const source$ = Observable.combineLatest(this.isSimulated$, isNotGameOrIsAdmin$);
        return source$.map(([isSimulated, isAdmin]) => isSimulated && isAdmin)
                      .startWith(false)
                      .publishReplay(1)
                      .refCount();
    }

    public $onInit() {
        this.watchScreenResize();
        this.watchResetAccount();
        this.account$
            .takeUntil(this.destroyed$)
            .map((a) => a.equityAccount)
            .filter(isNotNull)
            .subscribe((eq) => this.viewStore.updateAccount(eq));
    }

    private addRegistrationCallback(options: IGridOptions): IGridOptions {
        const gridCallback = options.onRegisterApi;

        options.onRegisterApi = (gridApi) => {
            if ((gridCallback != null) && (typeof gridCallback === "function")) {
                gridCallback(gridApi);
            }
            this.gridApi$.next(gridApi);
            gridApi.colMovable.on.columnPositionChanged!(this.$scope, () => this.persistTableState(gridApi));
            gridApi.core.on.sortChanged(this.$scope, () => this.persistTableState(gridApi));
            gridApi.core.on.columnVisibilityChanged(this.$scope, () => this.persistTableState(gridApi));
            this.$timeout(() => this.restoreTableState(gridApi));
        };

        return options;
    }

    private restoreTableState(gridApi: IGridApi) {
        const settings = this.$localStorage["equity-portfolio-positions"];
        if (settings != null && settings.data != null) {
            delete settings.data.grouping;
            gridApi.saveState.restore(this.$scope, settings.data);
        }
    }

    private persistTableState(gridApi: IGridApi) {
        const data = gridApi.saveState.save();
        delete data.grouping;

        this.$localStorage["equity-portfolio-positions"] = {
            version: 1,
            data
        };
    }

    private watchScreenResize() {
        this.systemEventService
            .eventsOfType(SystemEvent.VIEW_RESIZE)
            .withLatestFrom(this.gridApi$)
            .map(([_, api]) => api)
            .subscribe((api) => api.grid.handleWindowResize(undefined as any));
    }

    private watchResetAccount() {
        this.resetAccount$
            .withLatestFrom(this.accountStore.current)
            .map(([_, account]) => account)
            .filter(isNotNull)
            .map((a) => a.id)
            .map((id) => id as AccountId)
            .subscribe((id) => this.simulatedAccountService.reset(id));
    }

    public resetColumnFilters() {
        this.gridApi$
            .takeUntil(this.destroyed$)
            .take(1)
            .subscribe((gridApi) => {
                gridApi.grid.resetColumnSorting(null as any);
                gridApi.grid.clearAllFilters(true, true, true);
            });
    }

    public openTablePrefs() {
        this.gridApi$
            .takeUntil(this.destroyed$)
            .take(1)
            .subscribe((gridApi) => {
                this.openTablePreferences(gridApi);
            });
    }

    private openTablePreferences(gridApi: IGridApi) {
        this.$aside.open({
                             templateUrl: "/tradefloor/app/layout/tablePrefs/openPosPref.html",
                             placement: "right",
                             size: "sm",
                             backdrop: true,
                             controller: "TablePrefCtrl",
                             resolve: {
                                 gridApi: () => gridApi
                             }
                         });
    }

    public exportCsv() {
        this.gridApi$
            .takeUntil(this.destroyed$)
            .take(1)
            .subscribe((gridApi) => {
                gridApi.exporter.csvExport(this.uiGridExporterConstants.ALL, this.uiGridExporterConstants.ALL);
            });
    }

    public exportCsvVisible() {
        this.gridApi$
            .takeUntil(this.destroyed$)
            .take(1)
            .subscribe((gridApi) => {
                gridApi.exporter.csvExport(this.uiGridExporterConstants.VISIBLE, this.uiGridExporterConstants.VISIBLE);
            });
    }

    public async resetAccount() {
        try {
            await this.confirmationService.confirm("resetAccount");
            this.resetAccount$.next(true);
        } catch (e) {

        }
    }

    public averagePriceUpdated() {
        this.viewStore.reloadAveragePriceUpdates();
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
